// ./src/components/generic/ClientTypeSelector/ClientTypeSelector.tsx
import React, { useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";

const CLIENT_TYPE_OPTIONS = [
  {
    value: "individual",
    label: "Individual",
    description: "Client is an individual person",
    disabed: false,
  },
  {
    value: "company",
    label: "Company",
    description: "Client is a company",
    disabed: false,
  },
  {
    value: "organization",
    label: "Organization",
    description: "Client is a company",
    disabed: false,
  },
  {
    value: "government",
    label: "Government",
    description: "Client is a government organization",
    disabed: false,
  },
] as const;

export type ClientType = (typeof CLIENT_TYPE_OPTIONS)[number]["value"];

export interface ClientTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current content type value */
  value: string | null;

  required?: boolean;

  /** Callback when content type changes */
  onChange: (value: string | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;
}

export const ClientTypeSelector: React.FC<ClientTypeSelectorProps> = ({
  value,
  onChange,
  required,
  withIcons = true,
  withDescriptions = false,
  placeholder = "Select client type",
  ...selectProps
}) => {
  // Prepare select data with optional icons and descriptions
  const selectData = CLIENT_TYPE_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withIcons) {
      // Note: Mantine Select doesn't support custom icon rendering in options
      // This would need to be implemented with a custom component if icons are needed
      label = option.label;
    }

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // Auto-select first display type if required and no value is set
  useEffect(() => {
    if (required && !value && onChange) {
      const firstOption = CLIENT_TYPE_OPTIONS[0];
      if (firstOption && !firstOption.disabed) {
        onChange(firstOption.value);
      }
    }
  }, [required, value, onChange]);

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      searchable
      clearable={!required}
      required={required}
      size="md"
    />
  );
};

// Export the content type options for use in other components
export { CLIENT_TYPE_OPTIONS };
